// components/Cart.js
import { useState, useRef, useEffect } from 'react';
import { useCart } from '../hooks/cart';
import { toast } from 'react-toastify';
import { formatPrice } from '../hooks/generalHooks';
import SERVER from '../hooks/useServer';
import axios from 'axios';
import { LuChevronRight } from "react-icons/lu";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { loadStripe } from '@stripe/stripe-js';
import { FaRegTrashAlt } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { IoHeartOutline } from "react-icons/io5";
import cartClear from '../assets/icons/cart-clear.png'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next';

const Cart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { cart, removeFromCart, updateQuantity, cartTotal, clearCart, cartCount } = useCart();
  const [confirmed, setConfirmed] = useState(false);
  const [phoneCode, setPhoneCode] = useState('+33');
  const [formData, setFormData] = useState({name: '', email: '', sector:'', phone: '', address: '', codepostal: '', ville: '', notes: ''});
  const formRef = useRef(null);
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  const [selectedCountry, setSelectedCountry] = useState('FR');
  const [shippingMethod, setShippingMethod] = useState('standard');
  const [shippingOptions, setShippingOptions] = useState([]);
  const [openModal, setOpenModal] = useState(false)
  const [paymentModal, setPaymentModal] = useState(false)
  useEffect(() => {
    const fetchShippingOptions = async () => {
      try {
        const response = await axios.get(`${SERVER}/shipping/options`);
        setShippingOptions(response.data);
        if (response.data.length > 0) {
          const france = response.data.find(opt => opt.country_code === 'FR');
          if (france) {
            setSelectedCountry(france.country_code);
          }
        }
      } catch (error) {
        console.error('Error fetching shipping options:', error);
      }
    };
    
    fetchShippingOptions();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const scrollToForm = () => {
    setConfirmed(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const removeItemFromCart = (item) => {
    const toastId = 'remove-item-confirmation';
    toast.info(
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        width: '100%'
      }}>
        <div style={{ 
          flex: 1,
          textAlign: 'center',
          marginLeft: '-24px'
        }}>
          {t('cart.removeConfirm')}<br></br>{item.title}
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            marginTop: '0.5rem',
            justifyContent: 'center'
          }}>
            <button 
              onClick={() => {
                toast.dismiss();
                removeFromCart(item.id)
              }}
              style={{
                background: '#1B66A4',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                minWidth: '80px'
              }}
            >
              {t('common.yes')}
            </button>
            <button 
              onClick={() => toast.dismiss()}
              style={{
                background: '#f5f5f5',
                color: '#333',
                border: '1px solid #ddd',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                minWidth: '80px'
              }}
            >
              {t('common.no')}
            </button>
          </div>
        </div>
      </div>,
      {
        toastId : toastId, 
        position: "top-center",
        autoClose: false,
        closeButton: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        style: {
          borderRadius: '4px',
          padding: '1rem',
          minWidth: '300px'
        },
        bodyStyle: {
          padding: 0,
          margin: 0
        }
      }
    );
  }

  const handleCashOnDelivery = async () => {
    setPaymentModal(false);
    if (selectedCountry && formData.name && formData.email && formData.sector && phoneCode && formData.phone && formData.address && formData.ville && formData.codepostal) {
      const selectedCountryData = shippingOptions.find(option => 
        option.country_code === selectedCountry
      );
      const countryName = selectedCountryData?.country_name || selectedCountry;
      
      const orderData = {
        client: {
          name: formData.name,
          email: formData.email,
          sector: formData.sector,
          phone: phoneCode + " " + formData.phone,
          address: 'Pays: '+ countryName +'\nAdresse: '+ formData.address + '\nVille: ' + formData.ville + '\nCode Postal: ' + formData.codepostal,
        },
        items: cart.map(item => ({
          productId: item.id,
          title: item.title,
          price: item.price * (1 - (item.promotion / 100)) || item.price,
          quantity: item.quantity,
          calibrage: item.calibrage || 0,
          banner_image: item.banner_image
        })),
        notes: formData.notes || ''
      };
  
      try {
        const response = await axios.post(`${SERVER}/stripe/create-cod-order`, {
          client: orderData.client,
          items: orderData.items,
          notes: orderData.notes
        });
        
        if (response.data.success) {
          toast.success(t('cart.orderCreated', { orderNumber: response.data.orderNumber }));
          clearCart();
          navigate('/order-success', { state: { orderNumber: response.data.orderNumber } });
        }
      } catch (error) {
        toast.error(t('cart.orderError'));
        console.error('COD order error:', error);
      }
    } else {
      toast.info(t('cart.fillAllFields'));
    }
  };
  
  const handleStripePayment = async (e) => {
    setPaymentModal(false);
    e.preventDefault();
    
    if (selectedCountry && formData.name && formData.email && formData.sector && phoneCode && formData.phone && formData.address && formData.ville && formData.codepostal) {
      const selectedCountryData = shippingOptions.find(option => 
        option.country_code === selectedCountry
      );
      const countryName = selectedCountryData?.country_name || selectedCountry;
      
      try { 
        const orderData = {
          client: {
            name: formData.name,
            email: formData.email,
            sector: formData.sector,
            phone: phoneCode + " " + formData.phone,
            address: 'Pays: '+ countryName +'\nAdresse: '+ formData.address + '\nVille: ' + formData.ville + '\nCode Postal: ' + formData.codepostal,
          },
          items: cart.map(item => ({
            productId: item.id,
            title: item.title,
            price: item.price * (1 - (item.promotion / 100)) || item.price,
            quantity: item.quantity,
            calibrage: item.calibrage || 0,
            banner_image: item.banner_image
          })),
          notes: formData.notes || ''
        };
  
        const response = await axios.post(`${SERVER}/stripe/checkout`, {
          items: orderData.items,
          client: orderData.client
        });
        
        const { sessionId, orderNumber } = response.data;
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({ sessionId });
  
        if (error) {
          toast.error(t('cart.paymentError'));
          console.error('Checkout error:', error);
        } else {
          // Store order number in case the redirect fails
          localStorage.setItem('pendingOrder', orderNumber);
        }
      } catch (error) {
        toast.error(t('cart.submissionError'));
        console.error('Order submission error:', error);
      }
    } else {
      toast.info("Veuillez remplir tous les champs obligatoires.");
    }
  };  

  return (
    <div className="w-full max-w-7xl mx-auto px-4 lg:px-4 py-4 lg:py-8 pt-20 lg:pt-28">
      {/* Progress Steps */}
      <div className="flex items-center gap-2 lg:gap-3 mb-6 lg:mb-8 text-xs lg:text-sm overflow-x-auto">
        <div className="flex items-center gap-1 lg:gap-2 flex-shrink-0">
          <div className="w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-xs lg:text-sm">
            1
          </div>
          <span className="text-black font-medium whitespace-nowrap">{t('cart.title')}</span>
        </div>
        <LuChevronRight className="text-gray-400 flex-shrink-0" size={16} />
        <div className="flex items-center gap-1 lg:gap-2 flex-shrink-0">
          <div className={`w-5 h-5 lg:w-6 lg:h-6 rounded-full ${confirmed ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'} flex items-center justify-center font-semibold text-xs lg:text-sm`}>
            2
          </div>
          <span className={`whitespace-nowrap ${confirmed ? 'text-black font-medium' : 'text-gray-500'}`}>{t('cart.shipping')}</span>
        </div>
        <LuChevronRight className="text-gray-400 flex-shrink-0" size={16} />
        <div className="flex items-center gap-1 lg:gap-2 flex-shrink-0">
          <div className="w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center font-semibold text-xs lg:text-sm">
            3
          </div>
          <span className="text-gray-500 whitespace-nowrap">{t('cart.payment')}</span>
        </div>
      </div>

    
      
      {confirmed ? (
        <div ref={formRef} className="space-y-4 lg:space-y-6">
          {/* Page Title */}
          <h1 className="text-2xl lg:text-3xl font-normal text-black mb-4 lg:mb-6">{t('cart.coordinates')}</h1>
          
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            {/* Form Section */}
            <div className="flex-1 bg-white rounded-xl p-4 lg:p-6 shadow-sm">
              <form className="space-y-3 lg:space-y-4">
                {/* Name */}
                <div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder={t('cart.name') + '*'}
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 lg:px-4 lg:py-3 text-sm lg:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Email */}
                <div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder={t('cart.email') + '*'}
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 lg:px-4 lg:py-3 text-sm lg:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Sector */}
                <div>
                  <input
                    type="text"
                    id="sector"
                    name="sector"
                    placeholder={t('cart.sector') + '*'}
                    value={formData.sector}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 lg:px-4 lg:py-3 text-sm lg:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Country and Shipping Method */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
                  {/* Country Selector */}
                  <div>
                    <select 
                      value={selectedCountry}
                      onChange={(e) => {
                        const selected = shippingOptions.find(opt => opt.country_code === e.target.value);
                        setSelectedCountry(e.target.value);
                      }}
                      required
                      className="w-full px-3 py-2 lg:px-4 lg:py-3 text-sm lg:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white cursor-pointer appearance-auto"
                      style={{ WebkitAppearance: 'menulist', MozAppearance: 'menulist' }}
                    >
                      {shippingOptions.map(option => (
                        <option key={option.country_code} value={option.country_code}>
                          {option.country_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Shipping Method Selector */}
                  <div className="flex flex-col justify-center gap-2 px-0 lg:px-2">
                    {(() => {
                      const currentCountry = shippingOptions.find(opt => opt.country_code === selectedCountry);
                      return (
                        <>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="shippingMethod"
                              value="standard"
                              checked={shippingMethod === 'standard'}
                              onChange={() => setShippingMethod('standard')}
                              className="w-4 h-4 text-primary cursor-pointer"
                              style={{ accentColor: '#1B66A4' }}
                            />
                            <span className="text-sm">{t('cart.standard')}: €{formatPrice(currentCountry?.standard_fee || 0)}</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="shippingMethod"
                              value="express"
                              checked={shippingMethod === 'express'}
                              onChange={() => setShippingMethod('express')}
                              className="w-4 h-4 text-primary cursor-pointer"
                              style={{ accentColor: '#1B66A4' }}
                            />
                            <span className="text-sm">{t('cart.express')}: €{formatPrice(currentCountry?.express_fee || 0)}</span>
                          </label>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Phone Code and Phone Number */}
                <div className="grid grid-cols-2 gap-3 lg:gap-4">
                  <div>
                    <PhoneInput
                      defaultCountry="fr"
                      value={phoneCode}
                      onChange={(phone) => setPhoneCode(phone)}
                      className="w-full text-sm lg:text-base"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder={t('cart.phone') + '*'}
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 lg:px-4 lg:py-3 text-sm lg:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <input
                    id="address"
                    name="address"
                    placeholder={t('cart.deliveryAddress') + '*'}
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 lg:px-4 lg:py-3 text-sm lg:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Postal Code and City */}
                <div className="grid grid-cols-2 gap-3 lg:gap-4">
                  <div>
                    <input
                      type="text"
                      id="codepostal"
                      name="codepostal"
                      placeholder={t('cart.postalCode') + '*'}
                      value={formData.codepostal}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 lg:px-4 lg:py-3 text-sm lg:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <input
                      type='text'
                      id="ville"
                      name="ville"
                      placeholder={t('cart.city') + '*'}
                      value={formData.ville}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 lg:px-4 lg:py-3 text-sm lg:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <textarea
                    id="notes"
                    name="notes"
                    placeholder={t('cart.additionalNotes')}
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 lg:px-4 lg:py-3 text-sm lg:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>
              </form>
            </div>

            {/* Cart Summary Sidebar */}
            <div className="w-full lg:w-80 bg-white rounded-xl p-4 lg:p-6 shadow-sm h-fit lg:sticky lg:top-24">
              <h3 className="text-base lg:text-lg font-semibold text-black mb-3 lg:mb-4">
                {t('cart.myCart')} ({cart.reduce((acc, item) => acc + item.quantity, 0)}x)
              </h3>
              
              {/* Items List */}
              <div className="space-y-3 lg:space-y-4 mb-4 lg:mb-6 max-h-64 lg:max-h-96 overflow-y-auto">
                {cart.map(item => (
                  <div key={item.id} className="flex gap-2 lg:gap-3 pb-3 lg:pb-4 border-b border-gray-200 last:border-b-0">
                    <div className="w-12 h-12 lg:w-16 lg:h-16 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={`${SERVER}/${item.banner_image}`} 
                        alt={item.title}
                        className="w-full h-full object-contain p-1"
                      />
                    </div>
                    <div className="flex-1 text-xs lg:text-sm">
                      <p className="text-black font-medium mb-1 line-clamp-2">{item.title}</p>
                      {item?.promotion > 0 ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-primary font-bold">
                              € {formatPrice(item.price * (1 - item.promotion / 100))}
                            </span>
                            <span className="text-gray-400 line-through text-xs">
                              € {formatPrice(item.price)}
                            </span>
                          </div>
                          {item?.calibrage && parseFloat(item.calibrage) > 0 && (
                            <span className="text-xs text-gray-600">
                              {t('cart.calibration')}: € {formatPrice(item?.calibrage)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <span className="text-primary font-bold">
                            € {formatPrice(item?.price)}
                          </span>
                          {item?.calibrage && parseFloat(item.calibrage) > 0 && (
                            <span className="text-xs text-gray-600 block">
                              {t('cart.calibration')}: € {formatPrice(item?.calibrage)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-2 pt-3 lg:pt-4 border-t border-gray-200">
                <div className="flex justify-between text-xs lg:text-sm">
                  <span className="text-gray-600">{t('cart.subtotal')}:</span>
                  <span className="font-semibold">€ {formatPrice(cartTotal)}</span>
                </div>
                <div className="flex justify-between text-xs lg:text-sm">
                  <span className="text-gray-600">{t('cart.shippingFees')}:</span>
                  <span className="font-semibold">€7</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col lg:flex-row justify-end gap-3 lg:gap-4 mt-4 lg:mt-6">
            <button 
              type="button" 
              onClick={() => setConfirmed(false)}
              className="w-full lg:w-auto px-6 lg:px-8 py-2 lg:py-3 text-sm lg:text-base border border-black rounded-lg text-black hover:bg-gray-50 transition-colors order-2 lg:order-1"
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              onClick={() => setPaymentModal(true)}
              className="w-full lg:w-auto px-6 lg:px-8 py-2 lg:py-3 text-sm lg:text-base bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold order-1 lg:order-2"
            >
              {t('cart.proceedToPayment')}
            </button>
          </div>
        </div>
      ) : cart.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl shadow-sm">
          <img src={cartClear} alt="empty cart" className="w-32 h-32 mb-4 opacity-50" />
          <p className="text-xl text-gray-600 mb-2">{t('cart.emptyCart')}</p>
          <p className="text-gray-500 mb-6">{t('cart.restartShopping')}</p>
          <button 
            onClick={() => navigate('/products')}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('cart.viewProducts')}
          </button>
        </div>
      ) : (
        <>
          {/* Main Cart Container */}
          <div className="bg-white rounded-xl p-4 lg:p-6 max-w-5xl shadow-lg pb-16 lg:pb-24 mx-auto mb-6">
            {/* Page Title */}
            <h1 className="text-2xl lg:text-3xl font-normal text-black mb-2">
              {confirmed ? t('cart.coordinates') : t('cart.myCart')}
            </h1>
            
            {/* Cart Summary */}
            <h2 className="text-primary text-base lg:text-lg font-normal mb-3 lg:mb-4">{t('cart.orderSummary')}</h2>
            <div className="space-y-1 lg:space-y-2 text-black text-sm lg:text-base mb-4 lg:mb-6">
              <div className="flex gap-3 lg:gap-4">
                <span>{t('cart.subtotal')}:</span>
                <span className="font-bold">€ {formatPrice(cartTotal)}</span>
              </div>
              <div className="flex gap-3 lg:gap-4">
                <span>{t('cart.shippingFees')}:</span>
                <span className="font-bold">€7</span>
              </div>
            </div>

            {/* Cart Items */}
            <div className="space-y-3 lg:space-y-4">
              {cart.map(item => (
                <div key={item.id} className="border-t border-gray-200 pt-3 lg:pt-4 first:border-t-0 first:pt-0">
                  <div className="flex gap-3 lg:gap-4">
                    {/* Product Image */}
                    <div className="w-20 h-20 lg:w-36 lg:h-36 shadow-lg rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={`${SERVER}/${item.banner_image}`} 
                        alt={item.title}
                        className="w-full h-full object-contain p-1 lg:p-2"
                      />
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 lg:gap-12 mb-2">
                        <h3 className="text-black font-normal text-sm lg:text-lg flex-1 line-clamp-2">{item.title}</h3>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button 
                            className="text-gray-600 hover:text-red-500 transition-colors"
                            onClick={() => {/* Add to wishlist */}}
                          >
                            <IoHeartOutline size={18} className="lg:hidden" />
                            <IoHeartOutline size={20} className="hidden lg:block" />
                          </button>
                          <button 
                            onClick={() => removeItemFromCart(item)}
                            className="text-gray-600 hover:text-red-500 transition-colors"
                          >
                            <FaXmark size={18} className="lg:hidden" />
                            <FaXmark size={20} className="hidden lg:block" />
                          </button>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="mb-2 lg:mb-3">
                        {item?.promotion > 0 ? (
                          <div className="flex items-center gap-2">
                            <span className="text-primary text-base lg:text-xl font-bold">
                              € {formatPrice(item.price * (1 - item.promotion / 100))}
                            </span>
                            <span className="text-gray-400 line-through text-xs lg:text-sm">
                              € {formatPrice(item.price)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-primary text-base lg:text-xl font-bold">
                            € {formatPrice(item?.price)}
                          </span>
                        )}
                      </div>

                      {/* Quantity */}
                      <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-3">
                        <span className="text-black text-xs lg:text-sm">{t('cart.quantity')} :</span>
                        <span className="text-black font-normal text-xs lg:text-sm">{item.quantity}</span>
                      </div>

                      {/* Calibration if exists */}
                      {item?.calibrage && parseFloat(item.calibrage) > 0 && (
                        <div className="flex items-center gap-2 text-black text-xs lg:text-sm">
                          <span>{t('cart.calibration')} : € {formatPrice(item?.calibrage)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col lg:flex-row items-stretch lg:items-end justify-end gap-3 lg:gap-4 mt-4 lg:mt-6">
            <button 
              className="flex items-center justify-center gap-2 px-6 lg:px-12 py-2 lg:py-2 text-sm lg:text-base border border-black rounded-lg text-black hover:bg-gray-50 transition-colors order-2 lg:order-1"
              onClick={() => setOpenModal(true)}
            >
              <FaRegTrashAlt size={14} className="lg:hidden" />
              <FaRegTrashAlt size={16} className="hidden lg:block" />
              {t('cart.emptyCartButton')}
            </button>
            <button 
              className="flex items-center justify-center gap-2 px-6 py-2 text-sm lg:text-base bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold order-1 lg:order-2"
              onClick={scrollToForm}
            >
              {t('cart.continue')} 
              <LuChevronRight size={18} className="lg:hidden" />
              <LuChevronRight size={20} className="hidden lg:block" />
            </button>
          </div>
        </>
      )}

      {openModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          {cartCount > 0 ? (
              <div className="bg-white rounded-xl max-w-md w-full p-6 lg:p-8 relative shadow-2xl">
                <button className="absolute top-3 right-3 lg:top-4 lg:right-4 text-gray-400 hover:text-black transition-colors" onClick={() => setOpenModal(false)}>
                  <FaXmark size={20} className="lg:hidden" />
                  <FaXmark size={24} className="hidden lg:block" />
                </button>
                <div className="flex flex-col items-center text-center gap-3 lg:gap-4">
                  <FaRegTrashAlt className="text-gray-600" size={40} />
                  <span className="text-lg lg:text-xl text-black font-medium">{t('cart.deleteAllConfirm')}</span>
                  <div className="flex gap-3 lg:gap-4 mt-2 lg:mt-4 w-full">
                    <button 
                      onClick={() => setOpenModal(false)} 
                      className="flex-1 px-4 lg:px-6 py-2 lg:py-3 text-sm lg:text-base border border-black rounded-lg text-black hover:bg-gray-50 transition-colors"
                    >
                      {t('common.no')}
                    </button>
                    <button 
                      className="flex-1 px-4 lg:px-6 py-2 lg:py-3 text-sm lg:text-base bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold" 
                      onClick={() => {
                        clearCart();
                        setOpenModal(false);
                      }}
                    >
                      {t('cart.yesConfirm')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl max-w-md w-full p-6 lg:p-8 relative shadow-2xl">
                <button className="absolute top-3 right-3 lg:top-4 lg:right-4 text-gray-400 hover:text-black transition-colors" onClick={() => setOpenModal(false)}>
                  <FaXmark size={20} className="lg:hidden" />
                  <FaXmark size={24} className="hidden lg:block" />
                </button>
                <div className="flex flex-col items-center text-center gap-3 lg:gap-4">
                  <img src={cartClear} alt="cart-clear" className="w-20 h-20 lg:w-24 lg:h-24 opacity-50" />
                  <h1 className="text-xl lg:text-2xl text-black font-semibold">{t('cart.cartEmpty')}</h1>
                  <p className="text-sm lg:text-base text-gray-500">(0 {t('cart.items')})</p>
                  <span className="text-sm lg:text-base text-gray-600">{t('cart.restartShopping')}</span>
                  <div className="flex gap-3 lg:gap-4 mt-2 lg:mt-4 w-full">
                    <button 
                      className="flex-1 px-4 lg:px-6 py-2 lg:py-3 text-sm lg:text-base bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold" 
                      onClick={() => navigate('/')}
                    >
                      {t('cart.viewProducts')}
                    </button>
                  </div>
                </div>
              </div>
          )}
        </div>
      )}

      {paymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 lg:p-8 relative shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button 
              onClick={() => setPaymentModal(false)}
              className="absolute top-3 right-3 lg:top-4 lg:right-4 text-gray-400 hover:text-black transition-colors"
            >
              <FaXmark size={20} className="lg:hidden" />
              <FaXmark size={24} className="hidden lg:block" />
            </button>
            
            {/* Title */}
            <h2 className="text-xl lg:text-2xl font-bold text-black mb-4 lg:mb-6 pr-8">{t('cart.paymentMethod')}</h2>
            
            {/* Payment Options */}
            <div className="space-y-3 lg:space-y-4">
              {/* Stripe Payment Option */}
              <div 
                onClick={handleStripePayment}
                className="border-2 border-gray-200 rounded-xl p-4 lg:p-6 hover:border-primary cursor-pointer transition-all hover:shadow-md"
              >
                <div className="mb-3 lg:mb-4">
                  <h3 className="text-base lg:text-lg font-semibold text-black mb-1">{t('cart.payNow')}</h3>
                  <span className="text-xs lg:text-sm text-gray-600">{t('cart.secureCardPayment')}</span>
                </div>
                
                <div className="flex flex-wrap items-center gap-3 lg:gap-4 mb-3 lg:mb-4">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" 
                    alt="Stripe" 
                    className="h-6 lg:h-8 object-contain"
                  />
                  <div className="flex items-center gap-2">
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/9/98/Visa_Inc._logo_%282005%E2%80%932014%29.svg" 
                      alt="Visa" 
                      className="h-5 lg:h-6 object-contain"
                    />
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
                      alt="Mastercard" 
                      className="h-5 lg:h-6 object-contain"
                    />
                  </div>
                </div>
                
                <p className="text-[10px] lg:text-xs text-gray-500">{t('cart.redirectMessage')}</p>
              </div>
              
              {/* Divider */}
              <div className="flex items-center gap-3 lg:gap-4 my-4 lg:my-6">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="text-gray-500 text-xs lg:text-sm">{t('cart.or')}</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>
              
              {/* Cash on Delivery Option */}
              <div 
                onClick={handleCashOnDelivery}
                className="border-2 border-gray-200 rounded-xl p-4 lg:p-6 hover:border-primary cursor-pointer transition-all hover:shadow-md"
              >
                <div className="mb-3 lg:mb-4">
                  <h3 className="text-base lg:text-lg font-semibold text-black mb-1">{t('cart.payOnDelivery')}</h3>
                  <span className="text-xs lg:text-sm text-gray-600">{t('cart.cashOnDelivery')}</span>
                </div>
                
                <div className="flex items-center mb-3 lg:mb-4">
                  <img 
                    src="https://cdn-icons-png.flaticon.com/512/2703/2703983.png" 
                    alt="Cash" 
                    className="h-10 lg:h-12 object-contain"
                  />
                </div>
                
                <p className="text-[10px] lg:text-xs text-gray-500">{t('cart.cashMessage')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;